const READY_DISPOSITIONS = new Set(["interested", "qualified", "won", "converted"]);
const STALE_DISPOSITIONS = new Set(["follow_up", "callback", "nurture", "pending"]);
const OVERDUE_DISPOSITIONS = new Set(["not_interested", "dead", "lost", "do_not_contact", "closed_lost"]);

function normalizeDispositionKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function dispositionLabel(value) {
  const key = normalizeDispositionKey(value);
  if (!key) {
    return "Unknown";
  }
  const overrides = {
    follow_up: "Follow Up",
    not_interested: "Not Interested",
    do_not_contact: "Do Not Contact",
    closed_lost: "Closed Lost",
  };
  return overrides[key] || key.split("_").map((item) => item.charAt(0).toUpperCase() + item.slice(1)).join(" ");
}

function dispositionTone(key, count = 0) {
  if (!count && !key) {
    return "muted";
  }
  if (READY_DISPOSITIONS.has(key)) {
    return "ready";
  }
  if (STALE_DISPOSITIONS.has(key)) {
    return "stale";
  }
  if (OVERDUE_DISPOSITIONS.has(key)) {
    return "overdue";
  }
  return count > 0 ? "ready" : "muted";
}

function toPercent(value) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return `${Math.round(value * 100)}%`;
}

function parseDispositionAction(item) {
  const summary = String(item?.summary || "");
  const match = summary.match(/disposition set to ([^.]+)\.?/i);
  if (!match) {
    return null;
  }
  const key = normalizeDispositionKey(match[1]);
  return {
    key,
    label: dispositionLabel(key),
    conversationId: item?.conversation_id || null,
    contactName: item?.contact_name || "Unknown record",
    summary,
    occurredAt: item?.occurred_at || null,
    tone: dispositionTone(key, 1),
  };
}

export function buildDispositionBoard(report = {}, actions = []) {
  const totals = report?.totals || {};
  const counts = { ...(totals.disposition_counts || {}) };
  const actionRows = (actions || []).map(parseDispositionAction).filter(Boolean);

  if (!Object.keys(counts).length && actionRows.length) {
    for (const row of actionRows) {
      counts[row.key] = (counts[row.key] || 0) + 1;
    }
  }

  const cards = Object.entries(counts)
    .map(([rawKey, count]) => {
      const key = normalizeDispositionKey(rawKey);
      const latestAction = actionRows.find((item) => item.key === key) || null;
      return {
        key,
        label: dispositionLabel(key),
        count: Number(count || 0),
        rate: toPercent((totals.disposition_rates || {})[rawKey] ?? (totals.disposition_rates || {})[key]),
        tone: dispositionTone(key, Number(count || 0)),
        primaryConversationId: latestAction ? latestAction.conversationId : null,
        primaryConversationLabel: latestAction ? latestAction.contactName : null,
      };
    })
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));

  const dominantCard = cards[0] || null;
  const total = Number(totals.disposition_total || cards.reduce((sum, item) => sum + item.count, 0));

  return {
    summaryCards: [
      {
        label: "Outcome total",
        value: total,
        detail: cards.length ? `${cards.length} disposition bucket(s) tracked` : "No recorded outcomes in this window",
        tone: total > 0 ? "ready" : "muted",
      },
      {
        label: "Dominant outcome",
        value: dominantCard ? dominantCard.label : "No outcome leader",
        detail: dominantCard ? `${dominantCard.count} record(s)` : "Run dispositions from the CRM workspace to populate this board",
        tone: dominantCard ? dominantCard.tone : "muted",
      },
      {
        label: "Recent outcome changes",
        value: actionRows.length,
        detail: actionRows.length ? "Latest disposition saves from the live queue feed" : "No disposition actions were found in recent queue activity",
        tone: actionRows.length ? "ready" : "muted",
      },
    ],
    cards,
    recentRows: actionRows.slice(0, 6),
  };
}
